import type { CallGroupParticipant } from "@backstory/protocol";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useAuth } from "../../auth/store";
import { allowsFeature, useOrgEntitlements } from "../billing/entitlements";
import { EndUser } from "../../components/EndUser";
import { ErrorState, Skeleton } from "../../components/States";
import { PageHeader, RowMenu } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { callKeys, callsApi } from "./api";
import { GroupStatusBadge, QualityMeter, TopologyBadge, dur, pctText } from "./components";
import { CallUpgrade } from "./plan";
import "./calls.css";

export function CallGroupDetailPage() {
  const auth = useAuth();
  const ent = useOrgEntitlements(auth.currentOrgId);
  const { groupId } = useParams({ strict: false }) as { groupId: string };
  const navigate = useNavigate();
  const listSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const q = useQuery({ queryKey: callKeys.group(groupId), queryFn: () => callsApi.group(groupId) });
  const g = q.data;
  const backSearch: Record<string, unknown> = { ...listSearch, tab: "calls" };
  delete backSearch.view;
  const back = (
    <Link to="/calls" search={backSearch as never} className="btn ghost call-back">
      ← Calls
    </Link>
  );
  if (ent.isSuccess && !allowsFeature(ent.data, "webrtc_groups")) return <CallUpgrade kind="analytics" />;

  if (q.isLoading) {
    return (
      <div className="call-detail">
        <PageHeader back={back} title="Group call" />
        <Skeleton lines={6} />
      </div>
    );
  }
  if (q.isError || !g) {
    return (
      <div className="call-detail">
        <PageHeader back={back} title="Group call" />
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      </div>
    );
  }
  const openSession = (p: CallGroupParticipant) => void navigate({ to: "/sessions/$id", params: { id: p.sessionId }, search: { at: Date.parse(p.startedAt) } as never });
  return (
    <div className="call-detail">
      <PageHeader back={back} title={g.callGroupId} actions={<TopologyBadge topology={g.topology} />} />
      <section className="card calls-card call-head">
        <div className="call-head-q">
          <QualityMeter score={g.qualityScore} width={120} />
          <span className="call-head-badges">
            <GroupStatusBadge status={g.status} />
            {g.relayed && <span className="badge warn">relayed</span>}
            <span className="badge neutral">{g.active ? "In progress" : "Finished"}</span>
          </span>
        </div>
        <div className="call-groups">
          <section className="call-group" aria-label="Coverage">
            <h3>Coverage</h3>
            <dl className="call-kv">
              <div><dt>Participants</dt><dd>{g.participantCount}</dd></div>
              <div><dt>Reporting now</dt><dd>{g.reportingCount}</dd></div>
              <div><dt>Connections</dt><dd>{g.connectionCount}</dd></div>
              <div><dt>Duration</dt><dd>{dur(g.durationMs)}</dd></div>
            </dl>
          </section>
          <section className="call-group" aria-label="Attention">
            <h3>Attention</h3>
            <dl className="call-kv">
              <div><dt>Signals</dt><dd>{g.issueCount}</dd></div>
              <div><dt>Weakest</dt><dd>{g.worstParticipant || "—"}</dd></div>
              <div><dt>Started</dt><dd>{formatRelative(g.startedAt)}</dd></div>
            </dl>
          </section>
        </div>
      </section>
      <section className="card calls-list" aria-label="Participants">
        <div className="calls-list-head"><h2>Participants</h2></div>
        <table className="table calls-table group-table">
          <thead>
            <tr>
              <th>Participant</th>
              <th>Status</th>
              <th>Quality</th>
              <th>Loss</th>
              <th>Delay</th>
              <th>Jitter</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {g.participants.map((p) => (
              <tr key={`${p.participantId}-${p.sessionId}`} tabIndex={0} onClick={() => openSession(p)} onKeyDown={(e) => (e.key === "Enter" ? openSession(p) : undefined)}>
                <td>
                  <span className="calls-when"><EndUser id={p.endUserId || p.participantId} name={p.endUserName} /></span>
                  <div className="muted small">{p.role || "Participant"}{p.connectionCount > 1 ? ` · ${p.connectionCount} connections` : ""}</div>
                </td>
                <td><GroupStatusBadge status={p.status} /></td>
                <td><QualityMeter score={p.qualityScore} /></td>
                <td className={`num${(p.lossPctAvg ?? 0) >= 5 ? " bad" : ""}`}>{pctText(p.lossPctAvg)}</td>
                <td className="num">{p.rttP50Ms == null ? "—" : `${Math.round(p.rttP50Ms)} ms`}</td>
                <td className="num">{p.jitterMsAvg == null ? "—" : `${Math.round(p.jitterMsAvg)} ms`}</td>
                <td className="calls-row-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <ParticipantMenu participant={p} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ul className="call-card-list">
          {g.participants.map((p) => (
            <li key={`${p.participantId}-${p.sessionId}`} className="call-card" tabIndex={0} role="link" onClick={() => openSession(p)} onKeyDown={(e) => (e.key === "Enter" ? openSession(p) : undefined)}>
              <div className="call-card-head">
                <span className="call-card-when">
                  <strong>{p.endUserName || p.participantId}</strong>
                  <small className="muted">{p.role || "Participant"}</small>
                </span>
                <ParticipantMenu participant={p} />
              </div>
              <GroupStatusBadge status={p.status} />
              <QualityMeter score={p.qualityScore} width={120} />
              <dl className="call-card-stats">
                <div><dt>Loss</dt><dd className={(p.lossPctAvg ?? 0) >= 5 ? "bad" : undefined}>{pctText(p.lossPctAvg)}</dd></div>
                <div><dt>Delay</dt><dd>{p.rttP50Ms == null ? "—" : `${Math.round(p.rttP50Ms)} ms`}</dd></div>
                <div><dt>Jitter</dt><dd>{p.jitterMsAvg == null ? "—" : `${Math.round(p.jitterMsAvg)} ms`}</dd></div>
              </dl>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ParticipantMenu({ participant }: { participant: CallGroupParticipant }) {
  return (
    <RowMenu label={`Actions for ${participant.endUserName || participant.participantId}`}>
      <Link to="/sessions/$id" params={{ id: participant.sessionId }} search={{ at: Date.parse(participant.startedAt) } as never} role="menuitem">
        Open session replay
      </Link>
      <Link to="/calls/$callId" params={{ callId: participant.callId }} role="menuitem">
        Open connection
      </Link>
    </RowMenu>
  );
}
